import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, X, Menu, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';

const PricingPage: React.FC = () => {
  const { t, language } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annually'>('monthly');

  const plans = [
    {
      name: language === 'en' ? 'Starter' : 'البداية',
      price: billingPeriod === 'monthly' ? 29 : 290,
      originalPrice: billingPeriod === 'monthly' ? 29 : 348,
      features: {
        customers: 100,
        vehicleRecords: true,
        jobTracking: true,
        whatsappNotifications: true,
        paymentProcessing: true,
        basicReports: true,
        advancedAnalytics: false,
        multiLocation: false,
        apiAccess: false,
        dedicatedSupport: false,
        customIntegrations: false,
        prioritySupport: false,
        teamTraining: false,
      }
    },
    {
      name: language === 'en' ? 'Professional' : 'المحترف',
      price: billingPeriod === 'monthly' ? 59 : 590,
      originalPrice: billingPeriod === 'monthly' ? 59 : 708,
      popular: true,
      features: {
        customers: 500,
        vehicleRecords: true,
        jobTracking: true,
        whatsappNotifications: true,
        paymentProcessing: true,
        basicReports: true,
        advancedAnalytics: true,
        multiLocation: false,
        apiAccess: true,
        dedicatedSupport: false,
        customIntegrations: false,
        prioritySupport: true,
        teamTraining: true,
      }
    },
    {
      name: language === 'en' ? 'Enterprise' : 'المؤسسات',
      price: billingPeriod === 'monthly' ? 149 : 1490,
      originalPrice: billingPeriod === 'monthly' ? 149 : 1788,
      features: {
        customers: 'unlimited',
        vehicleRecords: true,
        jobTracking: true,
        whatsappNotifications: true,
        paymentProcessing: true,
        basicReports: true,
        advancedAnalytics: true,
        multiLocation: true,
        apiAccess: true,
        dedicatedSupport: true,
        customIntegrations: true,
        prioritySupport: true,
        teamTraining: true,
      }
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/">
                <h1 className="text-2xl font-bold" style={{ color: '#008B8B' }}>Rayyi7</h1>
              </Link>
            </div>

            <div className="flex items-center space-x-4">
              <div className="hidden md:flex items-center space-x-8">
              <Link to="/#features" className="text-gray-700 hover:text-[#008B8B] transition">
                {t('nav.features')}
              </Link>
              <Link to="/#about" className="text-gray-700 hover:text-[#008B8B] transition">
                {t('nav.about')}
              </Link>
              <Link to="/pricing" className="text-[#008B8B] font-medium">
                {t('nav.pricing')}
              </Link>
              <Link
                to="/login"
                className="px-6 py-2 rounded-lg font-medium text-white transition"
                style={{ backgroundColor: '#FF8C00' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#E67E00'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FF8C00'}
              >
                {t('nav.accessPlatform')}
              </Link>
              </div>

              <LanguageToggle />

              <button
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6 text-gray-700" />
                ) : (
                  <Menu className="h-6 w-6 text-gray-700" />
                )}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-3 space-y-3">
              <Link 
                to="/#features" 
                className="block text-gray-700 hover:text-[#008B8B] transition py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.features')}
              </Link>
              <Link 
                to="/#about" 
                className="block text-gray-700 hover:text-[#008B8B] transition py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.about')}
              </Link>
              <Link 
                to="/pricing" 
                className="block text-[#008B8B] font-medium py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.pricing')}
              </Link>
              <Link
                to="/login"
                className="block w-full text-center px-6 py-3 rounded-lg font-medium text-white transition"
                style={{ backgroundColor: '#FF8C00' }}
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.accessPlatform')}
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Pricing Header */}
      <section className="pt-16 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            {t('pricing.title')}
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            {t('pricing.subtitle')}
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center bg-gray-100 rounded-lg p-1">
            <button
              className={`px-6 py-2 rounded-md font-medium transition ${
                billingPeriod === 'monthly' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600'
              }`}
              onClick={() => setBillingPeriod('monthly')}
            >
              {t('pricing.monthly')}
            </button>
            <button
              className={`px-6 py-2 rounded-md font-medium transition ${
                billingPeriod === 'annually' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600'
              }`}
              onClick={() => setBillingPeriod('annually')}
            >
              {t('pricing.annually')} 
              <span className="ml-1 text-sm" style={{ color: '#10B981' }}>
                ({t('pricing.save')} 17%)
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* Free for Customers Banner */}
      <section className="pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-[#008B8B] to-[#006666] rounded-2xl p-8 text-white text-center">
            <h2 className="text-2xl font-bold mb-3">{t('pricing.freeForCustomers')}</h2>
            <p className="text-lg opacity-90">
              {t('pricing.customerAccess')}
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <div 
                key={index} 
                className={`bg-white rounded-2xl shadow-lg p-8 relative ${
                  plan.popular ? 'ring-2 ring-[#FF8C00] transform scale-105' : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-[#FF8C00] text-white px-4 py-1 rounded-full text-sm font-medium">
                      {t('pricing.mostPopular')}
                    </span>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{plan.name}</h3>
                  <div className="mb-2">
                    <span className="text-4xl font-bold" style={{ color: '#008B8B' }}>
                      {plan.price} {t('common.kwd')}
                    </span>
                    <span className="text-gray-600">
                      {billingPeriod === 'monthly' ? t('pricing.perMonth') : t('pricing.perYear')}
                    </span>
                  </div>
                  {billingPeriod === 'annually' && (
                    <p className="text-sm text-gray-500 line-through">
                      {plan.originalPrice} {t('common.kwd')}
                    </p>
                  )}
                  {billingPeriod === 'annually' && (
                    <p className="text-sm text-gray-500">
                      {t('pricing.billedAnnually')}
                    </p>
                  )}
                </div>

                <div className="space-y-4 mb-8">
                  <div className="pb-4 border-b border-gray-200">
                    <p className="font-medium text-gray-900">
                      {plan.features.customers === 'unlimited' 
                        ? t('pricing.unlimited') 
                        : plan.features.customers} {t('pricing.customersIncluded')}
                    </p>
                  </div>

                  <ul className="space-y-3">
                    <FeatureItem included={plan.features.vehicleRecords} text={t('pricing.vehicleRecords')} />
                    <FeatureItem included={plan.features.jobTracking} text={t('pricing.jobTracking')} />
                    <FeatureItem included={plan.features.whatsappNotifications} text={t('pricing.whatsappNotifications')} />
                    <FeatureItem included={plan.features.paymentProcessing} text={t('pricing.paymentProcessing')} />
                    <FeatureItem included={plan.features.basicReports} text={t('pricing.basicReports')} />
                    <FeatureItem included={plan.features.advancedAnalytics} text={t('pricing.advancedAnalytics')} />
                    <FeatureItem included={plan.features.multiLocation} text={t('pricing.multiLocation')} />
                    <FeatureItem included={plan.features.apiAccess} text={t('pricing.apiAccess')} />
                    <FeatureItem included={plan.features.prioritySupport} text={t('pricing.prioritySupport')} />
                    <FeatureItem included={plan.features.teamTraining} text={t('pricing.teamTraining')} />
                    <FeatureItem included={plan.features.dedicatedSupport} text={t('pricing.dedicatedSupport')} />
                    <FeatureItem included={plan.features.customIntegrations} text={t('pricing.customIntegrations')} />
                  </ul>
                </div>

                <Link
                  to="/login"
                  className={`block w-full text-center px-6 py-3 rounded-lg font-medium transition ${
                    plan.popular
                      ? 'bg-[#FF8C00] text-white hover:bg-[#E67E00]'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  {index === 2 ? t('pricing.contactUs') : t('pricing.getStarted')}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h4 className="text-2xl font-bold mb-4" style={{ color: '#FF8C00' }}>Rayyi7</h4>
              <p className="text-gray-400">{t('footer.partOfSlick')}</p>
              <p className="text-gray-400 mt-2">{t('footer.ownedBy')}</p>
            </div>
            <div>
              <h5 className="font-semibold mb-4">{t('footer.product')}</h5>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/#features" className="hover:text-white transition">{t('nav.features')}</Link></li>
                <li><Link to="/pricing" className="hover:text-white transition">{t('nav.pricing')}</Link></li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold mb-4">{t('footer.company')}</h5>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/#about" className="hover:text-white transition">{t('footer.aboutUs')}</Link></li>
                <li><a href="https://slickecosystem.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition">Slick Ecosystem</a></li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold mb-4">{t('footer.support')}</h5>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#help" className="hover:text-white transition">{t('footer.helpCenter')}</a></li>
                <li><a href="#docs" className="hover:text-white transition">{t('footer.documentation')}</a></li>
                <li><a href="tel:+96566477000" className="hover:text-white transition">+965 6647 7000</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-gray-800 text-center text-gray-400">
            <p>{t('footer.copyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

const FeatureItem: React.FC<{ included: boolean; text: string }> = ({ included, text }) => {
  return (
    <li className="flex items-center">
      {included ? (
        <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
      ) : (
        <X className="w-5 h-5 text-gray-300 mr-3 flex-shrink-0" />
      )}
      <span className={included ? 'text-gray-700' : 'text-gray-400'}>{text}</span>
    </li>
  );
};

export default PricingPage;